import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

import { CheckinHeader } from '@/components/checkin/CheckinHeader';
import { CheckinSection } from '@/components/checkin/CheckinSection';
import { OptionChipGroup } from '@/components/checkin/OptionChipGroup';
import { OptionCardGrid } from '@/components/checkin/OptionCardGrid';
import { WaterCounter } from '@/components/checkin/WaterCounter';
import { ProtocolStatusSelector, ProtocolStatus } from '@/components/checkin/ProtocolStatusSelector';
import { QuickReflectionChips } from '@/components/checkin/QuickReflectionChips';
import { StickySaveFooter } from '@/components/checkin/StickySaveFooter';
import { CheckinInsightCard } from '@/components/checkin/CheckinInsightCard';

const bloatingOptions = ["Ninguna", "Leve", "Moderada", "Alta"];
const hungerOptions = ["Muy desregulada", "Un poco desregulada", "Equilibrada"];
const energyOptions = ["Baja", "Moderada", "Buena", "Excelente"];
const bowelOptions = ["No fui al baño", "Fui con dificuldade", "Fui normalmente", "Fui más de una vez"];

const Checkin = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [bloating, setBloating] = useState('');
  const [hunger, setHunger] = useState('');
  const [energy, setEnergy] = useState('');
  const [bowel, setBowel] = useState('');
  const [water, setWater] = useState(0);
  const [protocol, setProtocol] = useState<ProtocolStatus>(null);
  const [reflections, setReflections] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [insight, setInsight] = useState<any>(null);
  const [checkinId, setCheckinId] = useState<string | null>(null);

  useEffect(() => {
    const fetchCheckin = async () => {
      if (!user || !user.id) return;
      
      try {
        const res = await fetch('/api/checkins/today');
        const { data } = await res.json();
          
        if (data) {
          setCheckinId(data.id);
          setBloating(data.bloating_level || '');
          setEnergy(data.energy_level || '');
          setBowel(data.poop_quality || '');
          setWater(data.water_glasses || 0);
          
          let parsedNotes = data.notes || '';
          let initialProtocol: ProtocolStatus = null;
          
          if (parsedNotes.includes('[Protocolo: Parcial]')) {
            initialProtocol = "parcial";
            parsedNotes = parsedNotes.replace('[Protocolo: Parcial]', '').trim();
          } else if (parsedNotes.includes('[Protocolo: Completado]')) {
            initialProtocol = "completado";
            parsedNotes = parsedNotes.replace('[Protocolo: Completado]', '').trim();
          }

          const reflectionMatch = parsedNotes.match(/\[Reflexiones: (.*?)\]/);
          if (reflectionMatch) {
            setReflections(reflectionMatch[1].split(', '));
            parsedNotes = parsedNotes.replace(reflectionMatch[0], '').trim();
          }
          
          setProtocol(initialProtocol);
          setNotes(parsedNotes.trim());
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCheckin();
  }, [user]);

  const generateInsight = () => {
    if (bloating === "Alta" && water < 4) {
      return {
        type: "hydration",
        title: "Día de hidratación",
        message: "Hinchazón alta y poca agua. Mañana prioriza beber más agua y optar por comidas ligeras para ayudar a tu digestión."
      };
    }
    if (bowel === "No fui al baño" && water < 4) {
      return {
        type: "movement",
        title: "Impulsa tu digestión",
        message: "Poca agua y falta de movimiento intestinal. Te sugerimos aumentar la fibra, beber más agua y hacer algo de movimiento ligero."
      };
    }
    if (protocol === "completado" && (energy === "Buena" || energy === "Excelente")) {
      return {
        type: "positive",
        title: "¡Sinergia perfecta!",
        message: "Completaste tu rutina y tienes buena energía. Sigue así, la consistencia está dando grandes frutos."
      };
    }
    return {
      type: "positive",
      title: "¡Check-in completado!",
      message: "Pequeños pasos crean grandes cambios. ¡Gran trabajo hoy!"
    };
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    
    let combinedNotes = "";
    if (protocol === "parcial") combinedNotes += "[Protocolo: Parcial]\n";
    else if (protocol === "completado") combinedNotes += "[Protocolo: Completado]\n";
    
    if (reflections.length > 0) combinedNotes += `[Reflexiones: ${reflections.join(', ')}]\n`;
    if (notes) combinedNotes += notes;

    const today = new Date().toLocaleDateString('en-CA');
    
    const checkinData = {
      bloating_level: bloating,
      energy_level: energy,
      poop_quality: bowel,
      water_glasses: water,
      notes: combinedNotes,
      checkin_date: today
    };

    try {
      const res = await fetch('/api/checkins/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: checkinId,
          payload: checkinData
        })
      });

      const { data, error } = await res.json();
      if (error) throw error;
      if (data) setCheckinId(data.id);
      
      setInsight(generateInsight());
      setSaved(true);
      toast({ title: "¡Guardado!", description: "Tu check-in fue guardado con éxito." });
    } catch (error: any) {
      console.error("Error saving checkin:", error);
      toast({ 
        title: "No pudimos guardar tu check-in", 
        description: error.message || "Inténtalo nuevamente.", 
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  const completedFields = [bloating, hunger, energy, bowel, water > 0].filter(Boolean).length;
  const totalRequiredFields = 5;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (saved && insight) {
    return <CheckinInsightCard insight={insight} onReset={() => setSaved(false)} />;
  }

  return (
    <div className="p-4 md:p-8 max-w-md mx-auto animate-fade-in pb-32">
      <CheckinHeader completedFields={completedFields} totalRequiredFields={totalRequiredFields} />

      <div className="space-y-6">
        <CheckinSection title="Estado del Cuerpo" step={1}>
          <OptionChipGroup label="Nivel de Hinchazón" options={bloatingOptions} selected={bloating} onSelect={setBloating} />
          <OptionChipGroup label="Equilibrio del Hambre" options={hungerOptions} selected={hunger} onSelect={setHunger} />
          <OptionChipGroup label="Nivel de Energía" options={energyOptions} selected={energy} onSelect={setEnergy} />
        </CheckinSection>

        <CheckinSection title="Digestión e Hidratación" step={2}>
          <OptionCardGrid label="Ida al Baño" options={bowelOptions} selected={bowel} onSelect={setBowel} />
          <div className="pt-2">
            <WaterCounter value={water} onChange={setWater} />
          </div>
        </CheckinSection>

        <CheckinSection title="Rutina Diaria" step={3}>
          <ProtocolStatusSelector status={protocol} onChange={setProtocol} />
        </CheckinSection>

        <CheckinSection title="Reflexión" step={4}>
          <QuickReflectionChips selected={reflections} onChange={setReflections} />
          <div className="space-y-2.5 pt-2">
            <h3 className="text-sm font-medium text-muted-foreground">Notas (opcional)</h3>
            <Textarea
              placeholder="¿Algo más que quieras anotar hoy?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-xl resize-none bg-background focus:ring-primary/20"
              rows={3}
            />
          </div>
        </CheckinSection>
      </div>

      <StickySaveFooter 
        completedFields={completedFields} 
        totalRequiredFields={totalRequiredFields} 
        onSave={handleSave} 
        saving={saving} 
      />
    </div>
  );
};

export default Checkin;